import React from 'react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage: rawCurrentPage, totalPages: rawTotalPages, onPageChange }) => {
    // Normalize values
    const totalPages = Math.max(1, Math.floor(rawTotalPages));
    const currentPage = Math.min(Math.max(1, Math.floor(rawCurrentPage)), totalPages);

    const handleFirst = (e: React.MouseEvent) => {
        e.preventDefault();
        if (currentPage > 1) {
            onPageChange(1);
        }
    };

    const handlePrevious = (e: React.MouseEvent) => {
        e.preventDefault();
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = (e: React.MouseEvent) => {
        e.preventDefault();
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleLast = (e: React.MouseEvent) => {
        e.preventDefault();
        if (currentPage < totalPages) {
            onPageChange(totalPages);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5; // Show only 4 pages at a time

        let startPage = Math.max(1, currentPage - 1);
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li className={`page-item ${i === currentPage ? 'active' : ''}`} key={i}>
                    <button
                        className="page-link"
                        onClick={(e) => {
                            e.preventDefault();
                            onPageChange(i);
                        }}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return pages;
    };

    return (
        <nav aria-label="Page navigation">
            <ul className="pagination justify-content-center mt-3">
                {/* First Page Button */}
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={handleFirst}>
                        «
                    </button>
                </li>

                {/* Previous Button */}
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={handlePrevious}>
                        ⟨
                    </button>
                </li>

                {/* Page Numbers - Limited to 4 pages */}
                {renderPageNumbers()}

                {/* Next Button */}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={handleNext}>
                        ⟩
                    </button>
                </li>

                {/* Last Page Button */}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={handleLast}>
                        »
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default Pagination;